import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Check, X, Pencil, UploadCloud } from "lucide-react";
import api from "@/lib/api";
import { Btn, Card, Pill, Head, Inp, Sel, Empty, errMsg, usePoll, useMeta, fmtDate, DC } from "@/components/datacenter/ui";

const show = (v) => (Array.isArray(v) ? v.join(", ") : v === true ? "yes" : v === false ? "no" : String(v));

function Suggestion({ s, meta, cats, selected, onSelect, onChange }) {
  const [edit, setEdit] = useState(false);
  const [vals, setVals] = useState(() => Object.fromEntries(Object.entries(s.values).map(([k, v]) => [k, show(v)])));
  const fields = meta.entities[s.entity].fields;
  const save = async () => {
    try { const r = await api.patch(`${DC}/ai/suggestions/${s.id}`, { values: vals }); onChange(r.data); setEdit(false); toast.success("Suggestion updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (status) => { const r = await api.patch(`${DC}/ai/suggestions/${s.id}`, { status }); onChange(r.data); };
  const setCat = async (category_id) => { const r = await api.patch(`${DC}/ai/suggestions/${s.id}`, { category_id }); onChange(r.data); };
  const done = s.status === "imported";
  return (
    <div className={`rounded-2xl border p-4 ${s.status === "rejected" ? "opacity-50 border-slate-200" : done ? "border-emerald-200 bg-emerald-50/30" : selected ? "border-[#165a71] bg-[#165a71]/5" : "border-slate-200"}`} data-testid={`ai-sug-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <label className="flex items-center gap-2">
          {!done && s.status !== "rejected" && <input type="checkbox" checked={selected} onChange={(e) => onSelect(e.target.checked)} data-testid={`ai-sug-select-${s.id}`} />}
          <Pill s={s.entity === "providers" ? "update" : s.entity === "services" ? "create" : "incomplete"}>{s.entity.slice(0, -1)}</Pill>
          <span className="font-semibold text-[#071b22]">{s.values.business_name || s.values.service_title || s.values.deal_title || s.values.provider_name}</span>
          {s.confidence != null && <span className="text-xs text-[#252A34]/50">{s.confidence}% confident</span>}
        </label>
        <div className="flex gap-1.5">
          {done ? <Pill s="created">{s.action}</Pill> : s.status === "rejected" ? <Btn kind="ghost" className="!py-1 !text-xs" onClick={() => setStatus("pending")} tid={`ai-sug-restore-${s.id}`}>Restore</Btn> : (
            <>
              <Btn kind="ghost" className="!py-1 !px-3 !text-xs" onClick={() => setEdit(!edit)} tid={`ai-sug-edit-${s.id}`}><Pencil className="h-3 w-3" /> Edit</Btn>
              <Btn kind="ghost" className="!py-1 !px-3 !text-xs" onClick={() => setStatus("rejected")} tid={`ai-sug-reject-${s.id}`}><X className="h-3 w-3" /> Reject</Btn>
            </>
          )}
        </div>
      </div>
      {s.duplicates?.length > 0 && <div className="mt-2 text-xs text-violet-700">Possible existing match: {s.duplicates.map((d) => `${d.name} (${d.match} ${d.confidence}%)`).join(", ")}</div>}
      {s.provider_ref?.status === "linked" && <div className="mt-1 text-xs text-emerald-700">Links to existing provider {s.provider_ref.name}</div>}
      {s.notes && <div className="mt-1 text-xs text-[#252A34]/60 italic">AI note: {s.notes}</div>}
      {!edit ? (
        <dl className="mt-3 grid gap-x-4 gap-y-1 sm:grid-cols-2 text-sm">
          {Object.entries(s.values).map(([k, v]) => <div key={k} className="flex gap-2"><dt className="text-[#252A34]/50 shrink-0">{fields.find((f) => f.key === k)?.label || k}:</dt><dd className="truncate">{show(v)}</dd></div>)}
        </dl>
      ) : (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {Object.keys(vals).map((k) => <label key={k} className="text-xs">{fields.find((f) => f.key === k)?.label || k}<Inp value={vals[k]} onChange={(e) => setVals((x) => ({ ...x, [k]: e.target.value }))} className="w-full" data-testid={`ai-edit-${s.id}-${k}`} /></label>)}
          <div className="sm:col-span-2 flex justify-end"><Btn onClick={save} tid={`ai-sug-save-${s.id}`}>Save</Btn></div>
        </div>
      )}
      {!done && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="text-[#252A34]/60">Category{s.category_confidence != null ? ` (${s.category_confidence}%${s.category_confidence < 75 ? " — review" : ""})` : ""}:</span>
          <Sel value={s.category_id || ""} onChange={setCat} options={[["", "None"], ...cats.map((c) => [c.id, c.label])]} tid={`ai-sug-cat-${s.id}`} className="!py-1 !text-xs max-w-[260px]" />
        </div>
      )}
    </div>
  );
}

function Session({ id, meta }) {
  const [s, setS] = useState(null);
  const [sel, setSel] = useState([]);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [link, setLink] = useState(true);
  const load = () => api.get(`${DC}/ai/sessions/${id}`).then((r) => setS(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => { const all = r.data || []; setCats(all.map((c) => ({ id: c.id, label: (all.find((p) => p.id === c.parent_id)?.name ? `${all.find((p) => p.id === c.parent_id).name} › ` : "") + c.name })).sort((a, b) => a.label.localeCompare(b.label))); }); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(load, s?.status === "processing", 2500);
  if (!s) return <div className="text-[#252A34]/60">Loading…</div>;
  const pending = (s.suggestions || []).filter((x) => x.status === "pending");
  const approve = async () => {
    setBusy(true);
    try { const r = await api.post(`${DC}/ai/sessions/${id}/approve`, { ids: sel, status: status || null, link_duplicates: link }); toast.success(`${r.data.created.length} records saved as drafts`); setSel([]); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="ai-session">
      <Card>
        <div className="text-xs text-[#252A34]/60">{s.file || "Pasted text"} · {fmtDate(s.created_at)} · {s.created_by_name}</div>
        <div className="mt-1 text-sm whitespace-pre-wrap line-clamp-3 text-[#252A34]/80">{s.input_preview}</div>
        {s.status === "processing" && <div className="mt-3 text-sm text-[#165a71] inline-flex items-center gap-2" data-testid="ai-processing"><Sparkles className="h-4 w-4 animate-pulse" /> AI is reading your data… this can take up to a minute.</div>}
        {s.status === "failed" && <div className="mt-3 text-sm text-rose-700" data-testid="ai-failed">{s.error}</div>}
        {s.detected?.length > 0 && <div className="mt-2 text-xs">Detected: {s.detected.join(", ")}</div>}
      </Card>
      {s.status === "ready" && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="font-display text-lg font-semibold">Review AI suggestions</div>
            <div className="flex gap-2 items-center flex-wrap">
              <Btn kind="ghost" onClick={() => setSel(sel.length === pending.length ? [] : pending.map((x) => x.id))} tid="ai-select-all">{sel.length === pending.length && pending.length ? "Deselect all" : "Select all"}</Btn>
              <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={link} onChange={(e) => setLink(e.target.checked)} data-testid="ai-link-dups" /> Fill existing matches instead of duplicating</label>
              <Sel value={status} onChange={setStatus} options={[["", "Save as imported draft"], ["pending_review", "Save as pending review"]]} tid="ai-approve-status" />
              <Btn kind="accent" onClick={approve} busy={busy} disabled={!sel.length} tid="ai-approve"><Check className="h-4 w-4" /> Import {sel.length || ""} selected</Btn>
            </div>
          </div>
          <div className="text-xs text-[#252A34]/60">Nothing is saved until you import. Records are created as drafts for review before publishing.</div>
          {(s.suggestions || []).length === 0 ? <Empty>No providers, services or deals were found in this content.</Empty> : (s.suggestions || []).map((x) => (
            <Suggestion key={x.id} s={x} meta={meta} cats={cats} selected={sel.includes(x.id)} onSelect={(on) => setSel((v) => (on ? [...v, x.id] : v.filter((y) => y !== x.id)))} onChange={(n) => setS((cur) => ({ ...cur, suggestions: cur.suggestions.map((y) => (y.id === n.id ? n : y)) }))} />
          ))}
        </>
      )}
    </div>
  );
}

export default function AIImportPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const meta = useMeta();
  const [text, setText] = useState("");
  const [hint, setHint] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  useEffect(() => { api.get(`${DC}/ai/sessions`).then((r) => setList(r.data.items)).catch(() => {}); }, [id]);
  const start = async () => {
    const fd = new FormData();
    if (text) fd.append("text", text);
    if (hint) fd.append("hint", hint);
    if (file) fd.append("file", file);
    setBusy(true);
    try { const r = await api.post(`${DC}/ai/sessions`, fd); nav(`${DC}/ai-import/${r.data.id}`); setText(""); setFile(null); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!meta?.entities) return <div className="text-[#252A34]/60">Loading…</div>;
  return (
    <div className="max-w-5xl space-y-5" data-testid="ai-import-page">
      <Head title="AI Import Assistant" sub="Paste messy lists, tables, directory text or upload CSV, Excel, PDF, Word or JSON. Claude proposes providers, services and deals — you review before anything is saved." />
      {id ? <><Link to={`${DC}/ai-import`} className="text-sm text-[#165a71]" data-testid="ai-back">← New AI import</Link><Session id={id} meta={meta} /></> : (
        <>
          <Card tid="ai-new">
            <textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder={"Emerald Coast Cleaning\nPanama City Beach\n850-555-1111\nVacation rental cleaning, deep cleaning and move-out cleaning. Serves PCB, 30A and Panama City."} className="w-full rounded-2xl border border-slate-200 p-3 text-sm" data-testid="ai-text" />
            <div className="mt-3 flex gap-2 flex-wrap items-center">
              <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm cursor-pointer" data-testid="ai-file-label"><UploadCloud className="h-4 w-4" />{file ? file.name : "Attach file"}<input type="file" className="hidden" accept=".csv,.tsv,.txt,.json,.xlsx,.xls,.pdf,.docx,.md" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ai-file" /></label>
              <Sel value={hint} onChange={setHint} options={[["", "Let AI detect the type"], ["providers", "Mostly providers"], ["services", "Mostly services"], ["deals", "Mostly deals"]]} tid="ai-hint" />
              <Btn onClick={start} busy={busy} disabled={!text.trim() && !file} className="ml-auto" tid="ai-start"><Sparkles className="h-4 w-4" /> Analyze with AI</Btn>
            </div>
          </Card>
          <div>
            <div className="text-sm font-semibold mb-2">Recent AI sessions</div>
            {list.length === 0 ? <Empty>No AI imports yet.</Empty> : <div className="space-y-2">{list.map((x) => (
              <Link key={x.id} to={`${DC}/ai-import/${x.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm hover:border-[#165a71]/40" data-testid={`ai-session-${x.id}`}>
                <span className="truncate max-w-[60%]">{x.kind === "generate_services" ? "✦ " : ""}{x.file || x.input_preview}</span>
                <span className="flex gap-2 items-center text-xs text-[#252A34]/60">{x.suggestion_count ?? "…"} suggestions · {fmtDate(x.created_at)} <Pill s={x.status === "ready" ? "ready" : x.status === "failed" ? "invalid" : "warning"}>{x.status}</Pill></span>
              </Link>
            ))}</div>}
          </div>
        </>
      )}
    </div>
  );
}
